import { initModals } from '@/scripts/snippets/modal';
import axios from 'axios';

import * as sections from '@shopify/theme-sections';
import ProductForm from '@savchukoleksii/shopify-product-form';
import { moneyWithCurrency } from '@/filters';
import { initAfterPay } from '@/scripts/components/afterpay';

import {
    disableBodyScroll,
    enableBodyScroll
} from '@/helpers/bodyScrollLockWrapper';

import { ITEM_PROPERTY_WITH_MEMBER_PRODUCT } from "@/store/minicart/shopify/cart/cart";

function _serializeOptionValues(inputs, transform) {
    return inputs.reduce(function (options, input) {
        if (
            input.checked || // If input is a checked (means type radio or checkbox)
            (input.type !== "radio" && input.type !== "checkbox") // Or if its any other type of input
        ) {
            options.push(transform({ name: input.name, value: input.value }));
        }

        return options;
    }, []);
}

// Override for correct options execution
ProductForm.prototype.options = function () {
    return _serializeOptionValues(this.optionInputs, function (item) {
        let regex = /(product\-[\d]+\-)?(options\[)?(?<name>.*?)(?:\])/;

        item.name = regex.exec(item.name).groups.name; // Use just the value between 'options[' and ']'

        return item;
    });
};

export const SECTION_NAME = 'quickview-new';

const selectors = {
    quickViewModal: `[data-quickview-modal]`,
    productForm: '[data-product-form]',
    productSelect: '[data-product-select]',
    productJson: '[data-product-json]',
    optionLabels: '[data-quickview-selected-value]',
    options: '[data-quickview-option-value]',
    variants: '[data-quickview-variant]',
    regularPrices: '[data-quickview-regular-price]',
    compareAtPrices: '[data-quickview-compare-at-price]',
    optionInput: '[data-single-option-selector]',
    memberPrice: '[data-quickview-member-price]',
    memberPriceTab: '[data-member-price-tab="membership"]',
    regularPriceTab: '[data-member-price-tab="regular"]',
    memberDiscountPrice: '[data-member-discount-value]',
    memberCompareAtPrice: '[data-quickview-member-compare-price]',
    memberInput: '[data-member-widget-input]',
    afterPay: `[data-afterpay-container]`,
    submitButton: '[data-add-variants-to-cart]',
    close: '[data-quickview-close]',
    opened: 'account-dashboard-modal--opened'
};

class QuickView {
    // data
    container = null;
    selectedOptions = null;
    memberDiscount = 0;
    product = null;
    selectedVariants = [];

    // elements
    quickViewModal = null;
    productForm = null;
    optionElements = [];
    optionLabels = [];
    variantsElements = [];
    regularPriceElements = [];
    compareAtPriceElements = [];
    memberRegularPriceElements = [];
    memberCompareAtPriceElements = [];
    memberDiscountPriceElements = [];
    optionInputs = [];

    constructor(container) {
        if (!container) {
            return;
        }

        this.container = container;

        const product = this.getProductJson();
        if (!product) {
            return;
        }

        this.product = product;

        this.memberDiscount = parseFloat(container?.dataset?.memberDiscount || '0.00');

        this.init(product);
        this.open();
    }

    init(product) {
        this.initQuickViewModal();
        this.initPrices();
        this.initLabelsElements();
        this.initVariantsElements();
        this.initOptionElements();
        this.initOptionInputsElements();

        this.initProductForm(product);
        this.initAfterPay();
        this.initAccordion();
        this.initVariantsSelect();
        this.initVariantsQantitySelect();

        this.initCloseElements();

        const container = this.container;
        if (!container) {
            return;
        }

        container.addEventListener("quickview:open", this.open.bind(this));
        container.addEventListener("quickview:close", this.open.bind(this));
    }

    initVariantsQantitySelect() {
        const quantityButtons = document.querySelectorAll('[data-quickview-variant] .account-dashboard-quantity-select__button');
        if (!quantityButtons) {
            return;
        }

        if (!quantityButtons?.length) {
            return;
        }

        quantityButtons.forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();

                this.onVariantQuantityChange(event?.currentTarget || event?.target);
            });
        });
    }

    initQuickViewModal() {
        const { container } = this;
        if (!container) {
            return;
        }

        const quickViewModal = container.querySelector(selectors?.quickViewModal);
        if (!quickViewModal) {
            return;
        }

        this.quickViewModal = quickViewModal;
    }

    initProductForm(product) {
        const { container } = this;
        if (!container) {
            return;
        }

        this.productForm = container?.querySelector(selectors?.productForm);
        if (!this.productForm) {
            return;
        }

        const onFormSubmit = this.onFormSubmit.bind(this);
        const onOptionChange = this.onOptionChange.bind(this);

        this.productForm = new ProductForm(
            this.productForm, product, {
                onFormSubmit,
                onOptionChange,
                selectors: {
                    idInput: '[name="id"]',
                    optionInput: '[data-single-option-selector]',
                    quantityInput: '[name="quantity"]',
                    propertyInput: '[name^="properties"]',
                    sellingPlanInput: '[name^="selling_plan"]',
                },
                onQuantityChange: () => {},
                onPropertyChange: () => {},
                onSellingPlanChange: () => {},
            }
        );
    }

    handleVariantSelect(btn) {
        const { container } = this;
        if (!container) {
            return;
        }

        const action = btn.dataset.quickviewVariantAction;
        if (action === 'add') {
            this.selectedVariants.push({
                quantity: 1,
                id: btn?.closest('[data-quickview-variant-id]')?.dataset?.quickviewVariantId,
            });

            btn.previousElementSibling.classList.remove('hidden');
            btn.classList.add('hidden');
        }

        if (this.selectedVariants.length > 0) {
            container?.querySelectorAll(selectors?.submitButton)?.forEach((submitButton) => {
                submitButton?.removeAttribute('disabled');
                submitButton.textContent = 'Add to cart';
            });
        } else {
            container?.querySelectorAll(selectors?.submitButton)?.forEach((submitButton) => {
                submitButton?.setAttribute('disabled', true);
                submitButton.textContent = 'Choose your version';
            });
        }
    }

    initVariantsSelect() {
        const variantSelectors = document.querySelectorAll('[data-quickview-variant-action]');
        if (!variantSelectors) {
            return;
        }

        if (!variantSelectors?.length) {
            return;
        }

        variantSelectors.forEach((variantSelector) => {
            variantSelector.addEventListener('click', (event) => {
                event.preventDefault();

                const { target } = event;
                if (!target) {
                    return;
                }

                this.handleVariantSelect(target);
            });
        });
    }

    initAfterPay() {
        initAfterPay(selectors?.afterPay, selectors?.productForm);
    }

    initAccordion() {
        const accordions = document.querySelectorAll('.account-dashboard-quick-view__tab');
        if (!accordions) {
            return;
        }

        if (!accordions?.length) {
            return;
        }

        accordions.forEach((accordion) => {
            const accordionButton = accordion.querySelector('.account-dashboard-quick-view__tab-title');
            if (!accordionButton) {
                return;
            }

            accordionButton.addEventListener('click', () => {
                accordion?.classList?.toggle('opened');
            });
        });
    }

    initLabelsElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const optionElements = container.querySelectorAll(selectors?.optionLabels);

        if (!optionElements) {
            return;
        }

        if (!optionElements?.length) {
            return;
        }

        const optionLabels = Array.prototype.slice.call(optionElements);
        if (!optionLabels) {
            return;
        }

        if (!optionLabels?.length) {
            return;
        }

        this.optionLabels = optionLabels;
    }

    initVariantsElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const variantElementNodes = container.querySelectorAll(selectors?.variants);
        if (!variantElementNodes) {
            return;
        }

        if (!variantElementNodes?.length) {
            return;
        }

        const variantElements = Array.prototype.slice.call(variantElementNodes);
        if (!variantElements) {
            return;
        }

        if (!variantElements?.length) {
            return;
        }

        this.variantsElements = variantElements;
    }

    initOptionElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const optionElementNodes = container.querySelectorAll(selectors?.options);
        if (!optionElementNodes) {
            return;
        }

        if (!optionElementNodes?.length) {
            return;
        }

        const optionElements = Array.prototype.slice.call(optionElementNodes);
        if (!optionElements) {
            return;
        }

        if (!optionElements?.length) {
            return;
        }

        this.optionElements = optionElements;
    }

    initCloseElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const closeElementNodes = container.querySelectorAll(selectors?.close);
        if (!closeElementNodes) {
            return;
        }

        if (!closeElementNodes?.length) {
            return;
        }

        const closeElements = Array.prototype.slice.call(closeElementNodes);
        if (!closeElements) {
            return;
        }

        if (!closeElements?.length) {
            return;
        }

        closeElements.forEach((closeElement) => {
            closeElement.addEventListener('click', () => {
                this.close();
            });
        });
    }

    initPrices() {
        this.initRegularPriceElements();
        this.initCompareAtPriceElements();
        this.initMemberRegularPriceElements();
        this.initMemberCompareAtPriceElements();
        this.initMemberDiscountPriceElements();
        this.addMemberPriceListener();
    }

    initRegularPriceElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const regularPriceElementNodes = container.querySelectorAll(selectors?.regularPrices);
        if (!regularPriceElementNodes) {
            return;
        }

        if (!regularPriceElementNodes?.length) {
            return;
        }

        const regularPriceElements = Array.prototype.slice.call(regularPriceElementNodes);
        if (!regularPriceElements) {
            return;
        }

        if (!regularPriceElements?.length) {
            return;
        }

        this.regularPriceElements = regularPriceElements;
    }

    initCompareAtPriceElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const compareAtPriceElementNodes = container.querySelectorAll(selectors?.compareAtPrices);
        if (!compareAtPriceElementNodes) {
            return;
        }

        if (!compareAtPriceElementNodes?.length) {
            return;
        }

        const compareAtPriceElements = Array.prototype.slice.call(compareAtPriceElementNodes);
        if (!compareAtPriceElements) {
            return;
        }

        if (!compareAtPriceElements?.length) {
            return;
        }

        this.compareAtPriceElements = compareAtPriceElements;
    }

    initMemberRegularPriceElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const memberRegularPriceElementNodes = container.querySelectorAll(selectors?.memberPrice);
        if (!memberRegularPriceElementNodes) {
            return;
        }

        if (!memberRegularPriceElementNodes?.length) {
            return;
        }

        const memberRegularPriceElements = Array.prototype.slice.call(memberRegularPriceElementNodes);
        if (!memberRegularPriceElements) {
            return;
        }

        if (!memberRegularPriceElements?.length) {
            return;
        }

        this.memberRegularPriceElements = memberRegularPriceElements;
    }

    initMemberCompareAtPriceElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const memberCompareAtPriceElementNodes = container.querySelectorAll(selectors?.memberCompareAtPrice);
        if (!memberCompareAtPriceElementNodes) {
            return;
        }

        if (!memberCompareAtPriceElementNodes?.length) {
            return;
        }

        const memberCompareAtPriceElements = Array.prototype.slice.call(memberCompareAtPriceElementNodes);
        if (!memberCompareAtPriceElements) {
            return;
        }

        if (!memberCompareAtPriceElements?.length) {
            return;
        }

        this.memberCompareAtPriceElements = memberCompareAtPriceElements;
    }

    initMemberDiscountPriceElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const memberDiscountPriceElementNodes = container.querySelectorAll(selectors?.memberDiscountPrice);
        if (!memberDiscountPriceElementNodes) {
            return;
        }

        if (!memberDiscountPriceElementNodes?.length) {
            return;
        }

        const memberDiscountPriceElements = Array.prototype.slice.call(memberDiscountPriceElementNodes);
        if (!memberDiscountPriceElements) {
            return;
        }

        if (!memberDiscountPriceElements?.length) {
            return;
        }

        this.memberDiscountPriceElements = memberDiscountPriceElements;
    }

    initOptionInputsElements() {
        const { container } = this;
        if (!container) {
            return;
        }

        const optionInputElementNodes = container.querySelectorAll(selectors?.optionInput);
        if (!optionInputElementNodes) {
            return;
        }

        if (!optionInputElementNodes?.length) {
            return;
        }

        const optionInputElements = Array.prototype.slice.call(optionInputElementNodes);
        if (!optionInputElements) {
            return;
        }

        if (!optionInputElements?.length) {
            return;
        }

        this.optionInputs = optionInputElements;
    }

    getProductJson() {
        const productJsonElement = this.container.querySelector(selectors?.productJson);
        if (!productJsonElement) {
            return null;
        }

        return JSON.parse(productJsonElement?.innerHTML?.trim() || 'null');
    }

    onFormSubmit(event) {
        const container = this.container;
        if (!container) {
            return;
        }

        event.preventDefault();

        const { selectedVariants } = this;
        if (!selectedVariants) {
            return;
        }

        if (!selectedVariants?.length) {
            return;
        }

        const input = container.querySelector(selectors?.memberInput);

        const submitButtons = container.querySelectorAll(selectors?.submitButton);
        if (!!submitButtons?.length) {
            submitButtons.forEach((submitButton) => {
                submitButton.disabled = true;
                submitButton.textContent = "Adding...";
                submitButton.classList.add('adding');
            });
        }

        document.dispatchEvent(new CustomEvent('minicart:product:add', {
            detail: {
                items: [
                    ...selectedVariants.map((item) => {
                        return {
                            id: Number(item?.id),
                            quantity: Number(item?.quantity),
                            ...(!!input?.checked ? {
                                properties: {
                                    [ITEM_PROPERTY_WITH_MEMBER_PRODUCT]: !!input?.checked,
                                }
                            } : {}),
                        };
                    })
                ],
                callback: () => {
                    if (!!submitButtons?.length) {
                        submitButtons.forEach((submitButton) => {
                            submitButton.disabled = false;
                            submitButton.textContent = "Choose your version";
                            submitButton.classList.remove('adding');
                        });
                    }

                    this.close();
                }
            }
        }));
    }

    changeSelectedVariantQuantity(id, quantity) {
        const selectedVariants = this.selectedVariants;

        if (quantity > 0) {
            this.selectedVariants = selectedVariants.map((variant) => {
                if (variant?.id === id) {
                    return {
                        ...variant,
                        quantity
                    };
                }

                return variant;
            });

            return;
        }

        this.selectedVariants = selectedVariants.filter((variant) => {
            return variant.id !== id;
        });
    }

    onVariantQuantityChange(target) {
        const variant_id = target?.closest('[data-quickview-variant-id]')?.dataset?.quickviewVariantId;
        if (!variant_id) {
            return;
        }

        if (!!target?.classList?.contains('account-dashboard-quantity-select__button--minus')) {
            const quantity = Number(target.nextElementSibling.value);
            if (quantity > 1) {
                target.nextElementSibling.value = quantity - 1;
                this.changeSelectedVariantQuantity(variant_id, quantity - 1);
            }

            if (quantity === 1) {
                target.parentElement.classList.add('hidden');

                target.parentElement.nextElementSibling.classList.remove('hidden');

                this.changeSelectedVariantQuantity(variant_id, 0);
            }
        } else {
            const quantity = Number(target.previousElementSibling.value);

            target.previousElementSibling.value = quantity + 1;

            this.changeSelectedVariantQuantity(variant_id, quantity + 1);
        }
    }

    addMemberPriceListener() {
        const { container } = this;
        if (!container) {
            return;
        }

        const input = container.querySelector(selectors?.memberInput);
        if (!input) {
            return;
        }

        input.addEventListener('change', () => {
            const memberPriceEL = container.querySelector(selectors?.memberPriceTab);
            if (!memberPriceEL) {
                return;
            }

            const regularPriceEL = container.querySelector(selectors?.regularPriceTab);
            if (!regularPriceEL) {
                return;
            }

            memberPriceEL?.classList?.toggle('product-form__price--selected');
            regularPriceEL?.classList?.toggle('product-form__price--selected');
        });
    }

    onOptionChange(event) {
        const { container } = this;
        if (!container) {
            return;
        }

        const { product } = this;
        if (!product) {
            return;
        }

        const variants = product?.variants;
        if (!variants) {
            return;
        }

        if (!variants?.length) {
            return;
        }

        const currentOptions = event?.dataset?.options?.reduce((
                selected_options, option, index
            ) => {
                return {
                    ...selected_options,
                    [`option${index + 1}`]: option?.value || null
                };
            },
            {});

        const keys = Object.keys(currentOptions);

        const conditions = [
            ...keys.map((key, index) => {
                return [...keys.slice(0, index)];
            }),
            keys
        ].reverse();

        const variant = conditions
            ?.map((condition) => {
                return variants?.find((variant) => {
                    return !condition
                        ?.map((key) => {
                            return variant[key] === currentOptions[key];
                        })
                        ?.includes(false);
                });
            })
            ?.find((variant) => {
                return Boolean(variant);
            });

        let selectedOptions = {
            ...(variant?.option1
                ? {
                    option1: variant?.option1
                }
                : {}),
            ...(variant?.option2
                ? {
                    option2: variant?.option2
                }
                : {}),
            ...(variant?.option3
                ? {
                    option3: variant?.option3
                }
                : {})
        };

        selectedOptions = Object.keys(selectedOptions).reduce((options, key) => {
                const value = selectedOptions[key];
                if (!value) {
                    return options;
                }

                return {
                    ...options,
                    [key]: value
                };
            },
            {});

        if (
            JSON.stringify(currentOptions) !== JSON.stringify(selectedOptions)
        ) {
            this.optionInputs.forEach((optionInput) => {
                const option = optionInput?.dataset?.index;
                if (!option) {
                    return;
                }

                optionInput.checked = false;
                optionInput.setAttribute('checked', undefined);

                const selectedValue = selectedOptions[option];
                if (!selectedValue) {
                    return;
                }

                const value = optionInput?.value;
                if (!value) {
                    return;
                }

                if (value !== selectedValue) {
                    return;
                }

                optionInput.checked = true;
                optionInput.setAttribute('checked', true);
            });
        }

        this.filterOptions(selectedOptions);
        this.filterVariants(selectedOptions);
        this.changesLabels(selectedOptions);
        this.changeOptionInfo(event, selectedOptions);
        this.updateImage(selectedOptions);
        this.updatePrices(variant);
        this.updateAfterPay(variant);
    }

    changeOptionInfo(event) {
        const current =
            event.target.parentElement.getAttribute('data-option-info');
        const infoEl = event.target
            .closest('.account-dashboard-quick-view__option')
            .querySelector('.account-dashboard-quick-view__option-text');
        infoEl.textContent = current;
    }

    changesLabels(selectedOptions) {
        const { optionLabels } = this;
        if (!optionLabels) {
            return;
        }

        if (!optionLabels?.length) {
            return;
        }

        for (const optionLabel of optionLabels) {
            const option = optionLabel?.dataset?.index;
            if (!option) {
                continue;
            }

            const value = selectedOptions[option];
            if (!value) {
                continue;
            }

            optionLabel.innerHTML = value;
        }
    }

    getFilteredOptions(selectedOptions) {
        const keys = Object.keys(selectedOptions);
        if (!keys) {
            return {};
        }

        const { product } = this;
        if (!product) {
            return {};
        }

        const variants = product?.variants;
        if (!variants) {
            return {};
        }

        if (!variants?.length) {
            return {};
        }

        const filtered_options = keys
            .map((key, index) => {
                return [...keys].splice(0, index);
            })
            .map((options) => {
                return variants?.filter((variant) => {
                    return !options
                        .map((key) => {
                            return variant[key] === selectedOptions[key];
                        })
                        .includes(false);
                });
            })
            .map((variants, index) => {
                return variants.map((variant) => {
                    return variant[`option${index + 1}`];
                });
            })
            .map((options) => {
                return [...new Set(options)];
            })
            .reduce((
                options_with_values, options, index
            ) => {
                return {
                    ...options_with_values,
                    [`option${index + 1}`]: options
                };
            }, {});

        if (!filtered_options) {
            return {};
        }

        return filtered_options;
    }

    filterOptions(selectedOptions) {
        const { optionElements } = this;
        if (!optionElements) {
            return;
        }

        if (!optionElements?.length) {
            return;
        }

        const filtered_options = this.getFilteredOptions(selectedOptions);
        if (!filtered_options) {
            return;
        }

        for (const optionElement of optionElements) {
            optionElement?.classList.add('hidden');

            const option = optionElement?.dataset?.index;
            if (!option) {
                continue;
            }

            const option_values = filtered_options[option];
            if (!option_values) {
                continue;
            }

            if (!option_values?.length) {
                continue;
            }

            const value = optionElement?.dataset?.value;
            if (!value) {
                continue;
            }

            if (!option_values?.includes(value)) {
                continue;
            }

            optionElement?.classList?.remove('hidden');
        }
    }

    filterVariants(selectedOptions) {
        const variantElements = this.variantsElements;
        if (!variantElements) {
            return;
        }

        if (!variantElements?.length) {
            return;
        }

        const { product } = this;
        if (!product) {
            return;
        }

        const variants = product?.variants;
        if (!variants) {
            return;
        }

        if (!variants?.length) {
            return;
        }

        const optionKeys = Object.keys(selectedOptions)?.slice(0, -1);

        const filtered_variants = variants?.filter((variant) => {
            return !optionKeys
                ?.map((key) => {
                    return variant[key] === selectedOptions[key];
                })
                ?.includes(false);
        });

        if (!filtered_variants) {
            return;
        }

        if (!filtered_variants?.length) {
            return;
        }

        const filtered_variant_ids = filtered_variants?.reduce((filtered_variant_ids, variant) => {
                const id = variant?.id;
                if (!id) {
                    return filtered_variant_ids;
                }

                return [...filtered_variant_ids, `${id}`];
            },
            []);

        if (!filtered_variant_ids) {
            return;
        }

        if (!filtered_variant_ids?.length) {
            return;
        }

        for (const variantElement of variantElements) {
            variantElement?.classList?.add('hidden');

            const variantElementId = variantElement?.dataset?.quickviewVariantId;
            if (!variantElementId) {
                continue;
            }

            if (!filtered_variant_ids.includes(variantElementId)) {
                continue;
            }

            variantElement?.classList?.remove('hidden');
        }
    }

    updatePrices(variant) {
        if (!variant) {
            return;
        }

        this.updateRegularPrices(variant);
        this.updateCompareAtPrices(variant);
    }

    updateRegularPrices(variant) {
        const { regularPriceElements } = this;
        if (!regularPriceElements) {
            return;
        }

        if (!regularPriceElements?.length) {
            return;
        }

        if (!variant) {
            return;
        }

        const price = variant?.price;

        regularPriceElements.forEach((regularPriceElement) => {
            regularPriceElement.innerHTML = moneyWithCurrency(price);
        });

        const { memberRegularPriceElements } = this;
        if (!memberRegularPriceElements) {
            return;
        }

        if (!memberRegularPriceElements.length) {
            return;
        }

        const { memberDiscount } = this;
        if (!memberDiscount) {
            return;
        }

        memberRegularPriceElements.forEach((compareAtPriceElement) => {
            compareAtPriceElement.innerHTML = moneyWithCurrency((price * (100 - memberDiscount)) / 100);
            compareAtPriceElement?.classList?.remove('hidden');
        });

        const { memberDiscountPriceElements } = this;
        if (!memberDiscountPriceElements) {
            return;
        }

        if (!memberDiscountPriceElements?.length) {
            return;
        }

        memberDiscountPriceElements.forEach((compareAtPriceElement) => {
            compareAtPriceElement.innerHTML = moneyWithCurrency((price * memberDiscount) / 100);
            compareAtPriceElement?.classList?.remove('hidden');
        });
    }

    updateCompareAtPrices(variant) {
        const { compareAtPriceElements } = this;
        if (!compareAtPriceElements) {
            return;
        }

        if (!compareAtPriceElements?.length) {
            return;
        }

        if (!variant) {
            return;
        }

        compareAtPriceElements.forEach((compareAtPriceElement) => {
            compareAtPriceElement?.classList?.add('hidden');
        });

        const compare_at_price = variant?.compare_at_price;
        if (!compare_at_price) {
            return;
        }

        const price = variant?.price;
        if (price >= compare_at_price) {
            return;
        }

        compareAtPriceElements.forEach((compareAtPriceElement) => {
            compareAtPriceElement.innerHTML =
                moneyWithCurrency(compare_at_price);
            compareAtPriceElement?.classList?.remove('hidden');
        });

        const { memberCompareAtPriceElements } = this;
        if (!memberCompareAtPriceElements) {
            return;
        }

        if (!memberCompareAtPriceElements.length) {
            return;
        }

        const { memberDiscount } = this;
        if (!memberDiscount) {
            return;
        }

        if (memberDiscount < 0) {
            return;
        }

        if (memberDiscount >= 100) {
            return;
        }

        memberCompareAtPriceElements.forEach((compareAtPriceElement) => {
            compareAtPriceElement.innerHTML = moneyWithCurrency((compare_at_price * (100 - memberDiscount)) / 100);
            compareAtPriceElement?.classList?.remove('hidden');
        });
    }

    getProductFeaturedImage() {
        const { product } = this;
        if (!product) {
            return null;
        }

        const featured_image = product?.featured_image;
        if (!featured_image) {
            return null;
        }

        return featured_image;
    }

    getImageBySelectedOptions(selectedOptions) {
        const { product } = this;
        if (!product) {
            return this.getProductFeaturedImage();
        }

        const images = product?.images;
        if (!images) {
            return this.getProductFeaturedImage();
        }

        if (!images?.length) {
            return this.getProductFeaturedImage();
        }

        const option_values = Object.keys(selectedOptions).map((key) => {
            return selectedOptions[key];
        });

        if (!option_values) {
            return this.getProductFeaturedImage();
        }

        if (!option_values?.length) {
            return this.getProductFeaturedImage();
        }

        const conditions = option_values
            .map((value, index) => {
                return [...option_values].slice(0, index);
            })
            .map((values) => {
                return values.join(' / ');
            })
            .filter((condition) => {
                return Boolean(condition);
            })
            .reverse();

        if (!conditions) {
            return this.getProductFeaturedImage();
        }

        if (!conditions?.length) {
            return this.getProductFeaturedImage();
        }

        const filtered_images = conditions
            .map((condition) => {
                return images.find((image) => {
                    return image?.alt
                        ?.toLowerCase()
                        .includes(condition?.toLowerCase());
                });
            })
            .filter((image) => {
                return Boolean(image);
            });

        if (!filtered_images) {
            return this.getProductFeaturedImage();
        }

        if (!filtered_images?.length) {
            return this.getProductFeaturedImage();
        }

        const image = filtered_images[0];
        if (!image) {
            return this.getProductFeaturedImage();
        }

        return image;
    }

    updateImage(selectedOptions) {
        const image = this.getImageBySelectedOptions(selectedOptions);
        if (!image) {
            return;
        }

        const src = image?.src;
        if (!src) {
            return;
        }

        const { container } = this;
        if (!container) {
            return;
        }

        const imageElements = container.querySelectorAll(`[data-quickview-image]`);
        if (!imageElements) {
            return;
        }

        if (!imageElements?.length) {
            return;
        }

        imageElements.forEach((imageElement) => {
            imageElement.src = src;
        });
    }

    open() {
        const { quickViewModal } = this;
        if (!quickViewModal) {
            return;
        }

        initModals();

        disableBodyScroll(quickViewModal, {
            reserveScrollBarGap: false,
            allowTouchMove(el) {
                while (el && el !== document?.body) {
                    if (el.getAttribute('data-allow-scroll') !== null) {
                        return true;
                    }

                    el = el?.parentElement;
                }
            }
        });

        quickViewModal?.classList?.add(selectors?.opened);
    }

    close() {
        const { quickViewModal } = this;
        if (!quickViewModal) {
            return;
        }

        quickViewModal?.classList?.remove(selectors?.opened);

        enableBodyScroll(quickViewModal);

        this.selectedVariants = [];

        const container = this.container;
        if (!container) {
            return;
        }

        container?.remove();
    }

    updateAfterPay(variant) {
        const container = this.container;
        if (!container) {
            return;
        }

        const afterPay = container.querySelector(selectors.afterPay);
        if (!afterPay) {
            return;
        }

        afterPay.dataset.price = variant.price;

        this.initAfterPay();
    }
}

sections.register(SECTION_NAME, {
    async onLoad() {
        const { container } = this;
        if (!container) {
            return;
        }

        new QuickView(container);
    },
});

document.addEventListener('DOMContentLoaded', () => {
    sections.load([SECTION_NAME]);
});

function initQuickViewModals() {
    document.addEventListener('click', async (event) => {
        let target = event?.target;
        if (!target?.hasAttribute('data-quickview-button')) {
            target = target?.closest('[data-quickview-button]');
        }

        if (!target) {
            return;
        }

        const handle = target?.dataset?.productHandle;
        if (!handle) {
            return;
        }

        const quickviewElement = document.querySelector(`[data-quickview][data-product-handle="${handle}"]`);
        if (!!quickviewElement) {
            quickviewElement.dispatchEvent(new CustomEvent("quickview:open"));

            return;
        }

        event.preventDefault();

        try {
            target.disabled = true;
            target?.classList?.add('loading');

            const response = await axios
                .get(`/products/${handle}?view=quickview`)
                .then((response) => {
                    return response?.data;
                });

            if (!response) {
                return;
            }

            const quickviewElement = document.createElement('div');
            quickviewElement.innerHTML = response?.toString();
            document.body.append(quickviewElement);

            sections.load([SECTION_NAME]);
        }
        catch (error) {}
        finally {
            target.disabled = false;
            target?.classList?.remove('loading');
        }
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initModals();

    initQuickViewModals();
});
